import { LegacyRef, memo, useEffect, useMemo } from "react";
import styled from "styled-components";
import { _ } from "@sablier/v2-mixins";
import { getTheme } from "@sablier/v2-themes";
import { FixedSizeList as List, areEqual } from "react-window";
import { Virtualized } from "~/components/molecules";
import type { ITableRow } from "@sablier/v2-types";
import type { ListChildComponentProps } from "react-window";
import Row from "../../Row";

const theme = getTheme();

const Static = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;
`;

function itemKey(index: number, data: { rows: ITableRow[] }) {
  return data.rows[index].id;
}

function Foo({
  data,
  index,
  style,
}: ListChildComponentProps<{ rows: ITableRow[] }>) {
  const row = data.rows[index];
  return <Row key={row.id} x={index} style={style} />;
}

const Item = memo(Foo, areEqual);

export const ContainerVirtualized = memo(function Container({
  isLoading,
  rows,
}: {
  isLoading: boolean;
  rows: ITableRow[];
}) {
  const count = useMemo(() => rows.length, [rows]);
  const height = useMemo(() => count * theme.sizes.tableRow, [count]);

  useEffect(() => {
    if (window.scrollY !== 0) {
      window.scrollBy(0, -1);
      window.scrollBy(0, 1);
    }
  }, [rows, isLoading]);

  return (
    <Virtualized.Scroller>
      {({ elementRef, containerRef, style, onScroll }) => (
        <List
          ref={elementRef as unknown as LegacyRef<List<{ rows: ITableRow[] }>>}
          outerRef={containerRef}
          style={style}
          height={height}
          itemCount={count}
          itemData={{ rows }}
          itemKey={itemKey}
          itemSize={theme.sizes.tableRow}
          onScroll={onScroll}
          width={"100%"}
        >
          {Item}
        </List>
      )}
    </Virtualized.Scroller>
  );
});

export const ContainerStatic = memo(function Container({
  rows,
}: {
  rows: ITableRow[];
}) {
  return (
    <Static data-component={"list"}>
      {rows.map((item, index) => (
        <Item key={item.id} data={{ rows }} index={index} style={{}} />
      ))}
    </Static>
  );
});
